import { Button } from "@shared/ui/Button";
import { TextInput } from "@shared/ui/TextInput";
import { Select } from "@shared/ui/Select";
import { Section } from "@shared/ui/Section";
import { Spinner } from "@shared/ui/Spinner";
import { FilePreviewLoader } from "@shared/ui/FilePreviewLoader";
import { notify } from "@shared/ui/toast/toastStore";
import { confirm } from "@shared/ui/confirm/confirmStore";
import { PaneHeader } from "@features/pane-manager/PaneHeader";
import { PaneFooter } from "@features/pane-manager/PaneFooter";
import { useScrollMemory } from "@features/pane-manager/useScrollMemory";
import {
    listSlideFormats,
    registerSlideFormat,
    reuploadSlideFormat,
    commitSlideFormat,
    uploadViaPresignedPost,
    downloadSlideFormat,
    PPTX_MIME,
    type SlideFormat,
} from "@entities/slide-format";
import { flushSettings, getCurrentFileBytes, getDocumentName } from "@features/office";
import { useState, useMemo, useRef } from "react";

type Phase = "idle" | "loading" | "busy";

/** 一覧の行に添える更新日時（年は省く。同名・似た名前の判別に足る粒度）。 */
function shortUpdatedAt(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const p = (n: number) => String(n).padStart(2, "0");
    return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** メタ行のサイズ表記。 */
function sizeLabel(bytes: number): string {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function FormatRegisterPan() {
    // 戻ってきたら離れたときの位置へ帰す（[[2026-09-04 設計ノート アドイン 戻ったときの表示位置]]）。
    const scroll = useScrollMemory();
    const [formats, setFormats] = useState<SlideFormat[]>([]);
    const [selectedId, setSelectedId] = useState("");
    const [name, setName] = useState("");
    const [phase, setPhase] = useState<Phase>("idle");
    const loadedRef = useRef(false);

    if (!loadedRef.current) {
        loadedRef.current = true;
        void (async () => {
            setPhase("loading");
            try {
                // サーバー既定が updatedAt 降順（`_DEFAULT_SORT = "updated"`）なので、そのまま並びに使う。
                setFormats(await listSlideFormats());
            } catch (e) {
                notify(
                    e instanceof Error ? e.message : "フォーマット一覧の取得に失敗しました",
                    "error"
                );
            }
            setPhase("idle");
        })();
    }

    const selected = useMemo(
        () => formats.find((f) => f.id === selectedId) ?? null,
        [formats, selectedId]
    );

    // **更新できるのは自分が登録したものだけ。** 一覧は持ち主に依らず全件返すのに、差し替えは
    // 所有者でなければ「スライドフォーマットが見つかりません」で拒まれる。全件を選択肢にしていた
    // ため、一覧に見えているのに更新できない、という食い違いがそのまま出ていた（記録 32）。
    // サーバーの意図は「他人のものは触らせない」で正しいので、触れないものを出さない側で直す。
    const own = useMemo(() => formats.filter((f) => f.isOwn !== false), [formats]);
    const hiddenCount = formats.length - own.length;

    const options = useMemo(
        () => [
            { value: "", label: "新規登録" },
            ...own.map((f) => ({
                value: f.id,
                // 未完了（uploading）は実体が無い＝プレビューも開けない。名前だけでは正常と見分けが
                // 付かないため、選択肢の段階で示す。
                label: `${f.name}${f.status === "uploading" ? "（未完了）" : ""}　${shortUpdatedAt(f.updatedAt)}`,
            })),
        ],
        [own]
    );

    /** 選択の切り替え。更新は既存名を初期値に入れる（触らなければ名前は変わらない）。 */
    function handleSelect(value: string) {
        setSelectedId(value);
        const target = formats.find((f) => f.id === value);
        setName(target ? target.name : "");
    }

    async function handleRegister() {
        // **置き換えは取り消せないので、先に確認を取る。** 選択欄は先頭が「新規登録」で以降が
        // 既存フォーマットという 1 つのドロップダウンなので、既存を選んだまま押すと確認なしで
        // ライブラリーのフォーマットが今のドキュメントに置き換わっていた（記録 52）。実体は
        // 同じ S3 キーへの上書きで、バケットにバージョニングは無いため元の内容は戻せない。
        if (selectedId) {
            const ok = await confirm({
                title: `「${selected?.name || "フォーマット"}」を現在のドキュメントで置き換えますか？`,
                body: "いま開いている PowerPoint の内容がフォーマットになります。元の内容は戻せません。",
                confirmLabel: selected?.status === "uploading" ? "再送信" : "更新",
                tone: "danger",
            });
            if (!ok) return;
        }
        setPhase("busy");
        try {
            // 実体を取り出す前に、生成設定の保存が出尽くすのを待つ（生成開始と同じ扱い・#109 #128）。
            // 保存は投げっぱなしなので、待たずに取り出すと直前の編集が入っていない pptx を登録し、
            // Web コンソールの生成が画面で見ている設定と食い違う。保存が失敗していれば投げて登録しない。
            await flushSettings();
            const bytes = await getCurrentFileBytes();
            const docName = await getDocumentName();
            const blob = new Blob([bytes as BlobPart], { type: PPTX_MIME });
            const finalName = name || docName || "Untitled";

            if (selectedId) {
                // 更新（未完了フォーマットの再送信もこの経路。reupload → S3 → commit で ready へ）。
                const { upload } = await reuploadSlideFormat(selectedId);
                await uploadViaPresignedPost(upload, blob);
                await commitSlideFormat(selectedId, { name: finalName });
                notify("フォーマットを更新しました", "success");
            } else {
                const { slideFormat, upload } = await registerSlideFormat({
                    name: finalName,
                    filename: `${docName || "format"}.pptx`,
                    size: bytes.byteLength,
                    contentType: PPTX_MIME,
                });
                await uploadViaPresignedPost(upload, blob);
                await commitSlideFormat(slideFormat.id, { name: finalName });
                notify("フォーマットを登録しました", "success");
            }
            loadedRef.current = false;
        } catch (e) {
            // ApiError は problem+json の detail を message に持つ（ガード違反の理由が出る）。
            notify(e instanceof Error ? e.message : "登録に失敗しました", "error");
        }
        setPhase("idle");
    }

    return (
        <>
            <PaneHeader title="フォーマット登録" />
            <div ref={scroll.ref} className="flex-1 overflow-y-auto">
                <div className="px-4 pt-4 pb-4">
                    <Section title="操作">
                        {phase === "loading" ? (
                            <div className="flex items-center gap-2 py-2 text-sm text-text-muted">
                                <Spinner /> 読み込み中...
                            </div>
                        ) : (
                            <Select
                                value={selectedId}
                                onChange={handleSelect}
                                options={options}
                                fullWidth
                            />
                        )}
                        {/* 隠したものがあることは伝える。黙って減らすと「登録したはずのものが
                            選べない」に見える（記録 32）。 */}
                        {hiddenCount > 0 && (
                            <p className="py-1 text-[11px] text-text-muted">
                                他の人が登録したフォーマット {hiddenCount}{" "}
                                件は更新できないため、選択肢に出していません。
                            </p>
                        )}
                        {selected && (
                            <p className="py-1 text-[11px] text-text-muted">
                                更新 {shortUpdatedAt(selected.updatedAt)} ・{" "}
                                {sizeLabel(selected.size)} ・{" "}
                                {selected.status === "ready" ? "利用可能" : "未完了"}
                            </p>
                        )}
                        {/* 未完了は実体が無いためプレビューは開けない。何が残っているかを示し、
                再送信へ導く。**「復旧」ではなく「今の内容で登録する」ことを言う。** 送るのは
                いま開いているドキュメントなので、別の deck を開いていれば中身がすり替わる
                （記録 52）。差し替えの失敗はここへ落ちてこない（サーバーは状態を落とさない）。 */}
                        {selected?.status === "uploading" && (
                            <p className="py-1 text-[11px] text-danger">
                                アップロードが完了していません。再送信すると、いま開いているドキュメントの内容で登録されます。
                            </p>
                        )}
                        {selected?.status === "ready" && (
                            <FilePreviewLoader
                                key={selected.id}
                                contentType={PPTX_MIME}
                                fileName={`${selected.name}.pptx`}
                                load={async () => (await downloadSlideFormat(selected.id)).blob}
                            />
                        )}
                    </Section>
                </div>
                <div className="px-4 pb-4">
                    <Section title="ファイル">
                        <div className="flex items-center gap-1.5 py-1">
                            <span className="shrink-0 rounded bg-sidebar-bg px-1.5 py-0.5 text-[11px] font-semibold text-text-muted">
                                PPTX
                            </span>
                            <span className="text-[13px]">現在のドキュメント</span>
                        </div>
                    </Section>
                </div>
                <div className="px-4 pb-4">
                    <Section title="フォーマット名">
                        <TextInput
                            value={name}
                            onChange={setName}
                            placeholder="名前を入力..."
                            fullWidth
                        />
                    </Section>
                </div>
            </div>
            <PaneFooter>
                <span className="flex-1" />
                <Button
                    variant="primary"
                    disabled={phase === "busy"}
                    onClick={() => void handleRegister()}
                >
                    {phase === "busy" ? (
                        <>
                            <Spinner /> 処理中...
                        </>
                    ) : selectedId ? (
                        selected?.status === "uploading" ? (
                            "再送信"
                        ) : (
                            "更新"
                        )
                    ) : (
                        "登録"
                    )}
                </Button>
            </PaneFooter>
        </>
    );
}
